import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Ui2TooltipMode } from '@innovation-os-ng/ui/ui2-tooltip';
import { ChipsLabel, TooltipTranslation, User } from '../../models/ui2-user-search.model';

const VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => Ui2UserSearchComponent),
  multi: true
};

const CHIP_HEIGHT = 40;

//offset height will make next chip slightly visible to make better user experience
const CHIP_CONTAINER_OFFSET_HEIGHT = 64;

@Component({
  selector: 'ui2-user-search',
  templateUrl: './ui2-user-search.component.html',
  styleUrls: ['./ui2-user-search.component.scss'],
  providers: [VALUE_ACCESSOR]
})
export class Ui2UserSearchComponent implements ControlValueAccessor, OnInit {
  /**
   * Hold users information
   */
  @Input()
  public users: Array<User>;

  /**
   * Whether to close the menu when a value is selected
   */
  @Input()
  public closeOnSelect: boolean = true;

  /**
   * Allows to hide selected users
   */
  @Input()
  public hideSelected: boolean = true;

  /**
   * Placeholder text for dropdown field
   */
  @Input()
  public placeholder: string = 'ui2UserSearchField.placeholder';

  /**
   * Minimum number of users which can be selected
   */
  @Input()
  public minCardinality: number;

  /**
   * Maximum number of users which can be selected
   */
  @Input()
  public maxCardinality: number;

  /**
   * Enabled or disable search field
   */
  @Input()
  public disabled: boolean;

  /**
   * Enable or disable mulitple selection
   */
  @Input()
  public isMultiSelect: boolean = true;

  /**
   * Number of visible chips which can be seen without scrolling
   */
  @Input()
  public maxVisibleChipsBeforeScroll: number = 4;

  /**
   * Tooltip text of dropdown field
   */
  @Input()
  public tooltipTranslation: TooltipTranslation;

  /**
   * Receives position Mode of the tooltip
   */
  @Input()
  public tooltipMode: Ui2TooltipMode = Ui2TooltipMode.BOTTOM;

  /**
   * Disable local search
   */
  @Input()
  public disableLocalSearch: boolean = false;

  /**
   * Hold the records of labels that need to be show for particular key in the users list
   */
  @Input()
  public chipsLabelMapper: Record<string, ChipsLabel>;

  /**
   * Show loader in the dropdown
   */
  @Input()
  public isLoading: boolean;

  // Emit the key that has been searched by the user
  @Output()
  public onSearch: EventEmitter<string> = new EventEmitter();

  // Emit the event when user reached to the end of option in the dropdown
  @Output()
  public scrollToEnd: EventEmitter<void> = new EventEmitter();

  // Fires when user opens the dropdown
  @Output()
  public onDropdownOpen: EventEmitter<void> = new EventEmitter();

  // Fires when user close the dropdown
  @Output()
  public onDropdownClose: EventEmitter<void> = new EventEmitter();

  /**
   *Contains chip container height
   */
  public chipsContainerHeight: number;

  /**
   *Contains selected users style
   */
  public selectedUsersStyle: Partial<CSSStyleDeclaration> = {};

  /**
   * Contains selected users
   */
  public selectedUsers: Array<User> | User;

  ngOnInit() {
    /**
     *Add height to chip container for scroll
     */
    this.chipsContainerHeight = CHIP_HEIGHT * this.maxVisibleChipsBeforeScroll + CHIP_CONTAINER_OFFSET_HEIGHT;

    this.selectedUsersStyle.maxHeight = this.chipsContainerHeight + 'px';
  }

  /**
   * Implement ControlValueAccessor interface methods
   */

  public onValueChange(selectedItems) {
    this.onChange(selectedItems);
  }

  public writeValue(user: Array<User>): void {
    this.selectedUsers = user;
  }

  public registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {}

  /**
   * Allows for custom search functionality based on disableLocalSearch flag
   *
   * @param term string
   * @param item User
   */
  public searchModel = (term: string, item: User) => {
    // If disabled local search, we do not perform any custom local search
    if (this.disableLocalSearch) {
      return true;
    }

    // make names into a single string to match on first and last name if necessary
    const firstAndLastName = `${item.firstName} ${item.lastName}`;
    // term to lowercase for matching purposes
    term = term.toLowerCase();
    // return any matched result
    return firstAndLastName.toLowerCase().indexOf(term) > -1 || item.email.toLowerCase().indexOf(term) > -1;
  };

  /**
   * Remove selected user
   *
   * @param user
   */
  public clearSelectedUser(user: User): void {
    //checks minSelection condition to ensure removal of selected user doesnot violate minimum selection requirement.
    if (!this.isBelowMinSelectionThreshold) {
      if (Array.isArray(this.selectedUsers)) {
        this.selectedUsers = this.selectedUsers.filter((selectedUser) => selectedUser.userUri !== user.userUri);
      } else {
        this.selectedUsers = this.isMultiSelect ? [] : null;
      }

      // Call onValueChange once after updating selectedUsers
      this.onValueChange(this.selectedUsers);
    }
  }

  /**
   * Method to disable user select based on the condition
   */
  public get isDisabled(): boolean {
    if (this.maxCardinality) {
      if (Array.isArray(this.selectedUsers)) {
        return this.disabled || this.checkMaxSelectionThreshold;
      } else {
        return this.disabled;
      }
    }
  }

  /**
   * Holds flag for minimum selection
   */
  public get isBelowMinSelectionThreshold(): boolean {
    // condition to check if selectedUsers is an array or not
    if (Array.isArray(this.selectedUsers)) {
      //checks length is less than or equal to minimum cardinality
      return this.selectedUsers.length <= this.minCardinality;
    } else {
      // checks selectedUsers and minCardinality is truthy
      return !!this.selectedUsers && !!this.minCardinality;
    }
  }

  /**
   * Emit search keyword to the host component
   *
   * @param searchTerm
   */
  public search(searchEvent: { term: string; items: Array<User> }): void {
    this.onSearch.emit(searchEvent.term);
  }

  /**
   * Holds flag for maximum selection
   *
   * @returns
   */
  public get checkMaxSelectionThreshold(): boolean {
    if (Array.isArray(this.selectedUsers)) {
      return !!(this.selectedUsers?.length >= this.maxCardinality);
    }
  }

  private onChange: (user: User) => void = () => {};
}
